import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Users, Wallet } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { PageLoader } from "@/components/brand/PageLoader";
import { formatMoneyMinor } from "@/lib/money";

interface CourtRow {
  id: string;
  name: string;
  format: string;
  base_price_minor: number | null;
  currency: string;
  description: string | null;
  venue: {
    venue_name: string;
    city: string;
    region: string;
    country: string;
  } | null;
  open_slot_count: { count: number }[] | null;
}

/**
 * Public /courts browser. Players land here to find a venue + time
 * and either host a new match on an open slot or join one already in
 * progress. RLS shields unpublished courts so we can safely trust the
 * public role to read this.
 */
export function CourtsPage() {
  const [courts, setCourts] = useState<CourtRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // PostgREST embed: venue row + count of currently-open slots.
      // We only want courts that actually have slots available to
      // book, so the empty array check below filters them out.
      const { data, error } = await supabase
        .from("courts")
        .select(`
          id, name, format, base_price_minor, currency, description,
          venue:venue_applications ( venue_name, city, region, country ),
          open_slot_count:availability_slots ( count )
        `)
        .eq("published", true)
        .eq("availability_slots.status", "open")
        .gte("availability_slots.starts_at", new Date().toISOString())
        .order("name");

      if (cancelled) return;
      if (error) {
        setError(error.message);
        setCourts([]);
        return;
      }
      setCourts((data ?? []) as CourtRow[]);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (courts === null) return <PageLoader />;

  return (
    <section>
      <div className="mx-auto max-w-6xl px-6 pt-16 pb-10">
        <p className="mb-3 text-xs font-bold uppercase tracking-widest text-volt">
          Find a run
        </p>
        <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl">
          Courts ready to book.
        </h1>
        <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
          Every court on Whistll is verified, Stripe-backed, and ready
          to host pickup games. Pick a court to see open slots and who
          else is playing.
        </p>
      </div>

      <div className="mx-auto max-w-6xl px-6 pb-24">
        {error && (
          <p className="mb-6 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
            {error}
          </p>
        )}
        {courts.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card/40 p-12 text-center">
            <p className="text-sm font-semibold">No courts available yet.</p>
            <p className="mt-2 text-xs text-muted-foreground">
              We're onboarding venues right now - check back soon.
            </p>
          </div>
        ) : (
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {courts.map((c) => {
              const openCount = c.open_slot_count?.[0]?.count ?? 0;
              return (
                <li key={c.id}>
                  <Link
                    to={`/courts/${c.id}`}
                    className="flex h-full flex-col rounded-2xl border border-border bg-card p-5 transition hover:border-volt/60"
                  >
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-semibold">{c.name}</h2>
                      <span className="rounded-full bg-volt-subtle px-2 py-0.5 text-[10px] font-bold text-volt">
                        {c.format}
                      </span>
                    </div>
                    {c.venue && (
                      <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        {c.venue.venue_name} · {c.venue.city}
                      </p>
                    )}
                    {c.description && (
                      <p className="mt-3 line-clamp-3 text-sm text-muted-foreground">
                        {c.description}
                      </p>
                    )}
                    <div className="mt-auto flex items-center justify-between pt-4 text-xs">
                      <span className="inline-flex items-center gap-1 text-muted-foreground">
                        <Users className="h-3 w-3" /> {openCount} open slot
                        {openCount === 1 ? "" : "s"}
                      </span>
                      {c.base_price_minor != null && (
                        <span className="inline-flex items-center gap-1 font-semibold">
                          <Wallet className="h-3 w-3" />
                          {formatMoneyMinor(c.base_price_minor, c.currency)}
                          <span className="ml-0.5 font-normal text-muted-foreground">
                            / player
                          </span>
                        </span>
                      )}
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
